import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import useSetupGuard from '@/hooks/use-setup-guard';
import LoadingLayer from '@/components/LoadingLayer';

interface SetupGuardProps {
  children: ReactNode;
  /**
   * Routes that should be accessible even when setup is required
   */
  allowedRoutes?: string[];
  /**
   * Whether to show loading spinner while checking setup status
   */
  showLoading?: boolean;
}

const SetupGuard = ({ 
  children, 
  allowedRoutes = ['/verification-setup', '/verification-choose', '/sign-out', '/callback'],
  showLoading = true 
}: SetupGuardProps) => {
  const location = useLocation();
  const { 
    isLoading, 
    setupRequired, 
    setupCompleted, 
    enforceSetupRequirement 
  } = useSetupGuard();

  // Check if current route is allowed during setup
  const isAllowedRoute = allowedRoutes.some(route => 
    location.pathname.startsWith(route)
  );

  // Enforce setup requirement
  useEffect(() => {
    if (!isLoading && !isAllowedRoute) {
      enforceSetupRequirement(location.pathname);
    }
  }, [isLoading, isAllowedRoute, location.pathname, enforceSetupRequirement]);

  // Show loading spinner while checking setup status
  if (isLoading && showLoading) {
    return <LoadingLayer />;
  }

  // If setup is required and route is not allowed, don't render children
  // The enforceSetupRequirement will handle the redirect
  if (setupRequired && !setupCompleted && !isAllowedRoute) {
    return <LoadingLayer />;
  }

  return <>{children}</>;
};

export default SetupGuard;